import React, { useCallback, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import useToggle from 'hooks/useToggle'
import { pluralizeToString } from 'lib/string/pluralize'
import FormatCurrency from 'components/Common/FormatCurrency'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { formatRoomType } from 'lib/tours/tourUtils'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'components/Luxkit/Divider'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import Group from 'components/utils/Group'
import DropdownList from 'components/Luxkit/Dropdown/List/DropdownList'
import { checkCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import useAgentHubCommission from 'agentHub/hooks/useAgentHubCommission'

const BreakdownList = styled(VerticalSpacer)`
  padding-inline: ${rem(16)};
`

const SystemMessaging = styled.div`
  display: flex;
  padding: ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
`

interface Props {
  purchasableOptionsPriceBreakdowns: Array<[Tours.TourV2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown]>
  totalPrice: number
  isLeTour: boolean
  offerId: string
  offerLocations: Array<string>
}

function PricingBreakdownDropdown(props: Props) {
  const {
    purchasableOptionsPriceBreakdowns,
    totalPrice,
    isLeTour,
    offerId,
    offerLocations,
  } = props

  const triggerRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, toggleDropdown, , closeDropdown] = useToggle()

  const doNotBubble = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const handleToggleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    doNotBubble(event)
    toggleDropdown()
  }, [toggleDropdown, doNotBubble])

  const canViewLuxPlusBenefits = useAppSelector(checkCanViewLuxPlusBenefits)
  const { commissionAmount } = useAgentHubCommission({
    productType: 'tour',
    offerId,
    offerLocations,
  })
  const customerView = useAppSelector(state => state.agentHub.customerView)
  const showAgentView = config.agentHub.isEnabled && !customerView && !!commissionAmount
  const commissionTotal = totalPrice * (commissionAmount / 100)

  return (
    <>
      <SmallToggleButton
        on={isDropdownOpen}
        onClick={handleToggleClick}
        ref={triggerRef}
        size="small"
      >
        Price details
      </SmallToggleButton>
      <DropdownList
        size="S"
        open={isDropdownOpen}
        placement="bottom-start"
        title="Price details"
        anchorRef={triggerRef}
        triggerRef={triggerRef}
        onClose={closeDropdown}
      >
        <BreakdownList gap={8}>
          {purchasableOptionsPriceBreakdowns.map(
            ([purchasableOption, priceBreakdown], index) => (
              <VerticalSpacer key={index} gap={4}>
                <Group direction="horizontal" horizontalAlign="space-between">
                  <BodyText variant="medium" weight="bold" forwardedAs="span">{formatRoomType(purchasableOption.roomType)}</BodyText>
                  <BodyText variant="medium" weight="bold" forwardedAs="span">
                    <FormatCurrency value={ canViewLuxPlusBenefits ? priceBreakdown.totalMemberPrice || priceBreakdown.totalPrice : priceBreakdown.totalPrice} />
                  </BodyText>
                </Group>
                { canViewLuxPlusBenefits &&
                  priceBreakdown.adultsMemberPriceBreakdown.map(
                    (adultPrice, index) => (
                      <Group key={index} direction="horizontal" horizontalAlign="space-between">
                        <BodyText variant="medium" forwardedAs="span">{isLeTour ? 'Traveller' : 'Adult'} {index + 1}</BodyText>
                        <BodyText variant="medium" forwardedAs="span">
                          <FormatCurrency value={adultPrice || priceBreakdown.adultsPriceBreakdown[index]} />
                        </BodyText>
                      </Group>
                    ),
                  )}
                {
                  !canViewLuxPlusBenefits &&
                  priceBreakdown.adultsPriceBreakdown.map(
                    (adultPrice, index) => (
                      <Group key={index} direction="horizontal" horizontalAlign="space-between">
                        <BodyText variant="medium" forwardedAs="span">{isLeTour ? 'Traveller' : 'Adult'} {index + 1}</BodyText>
                        <BodyText variant="medium" forwardedAs="span">
                          <FormatCurrency value={adultPrice} />
                        </BodyText>
                      </Group>
                    ),
                  )}
                {priceBreakdown.childrenPriceBreakdown.map(
                  (childPrice, index) => (
                    <Group key={index} direction="horizontal" horizontalAlign="space-between">
                      <BodyText variant="medium" forwardedAs="span">Child</BodyText>
                      <BodyText variant="medium" forwardedAs="span">
                        <FormatCurrency value={childPrice} />
                      </BodyText>
                    </Group>
                  ),
                )}
                {priceBreakdown.childrenPriceBreakdown.length >
                  priceBreakdown.appliedChildDiscounts && (
                    <SystemMessaging>
                      <CaptionTextBlock startIcon={<LineInfoCircleIcon />} variant="medium">
                        Child discount applies to{' '}
                        {pluralizeToString(
                          'child',
                          priceBreakdown.appliedChildDiscounts,
                        )}{' '}
                        only, remainder will be charged as an adult.
                      </CaptionTextBlock>
                    </SystemMessaging>
                )}
              </VerticalSpacer>
            ),
          )}
          <Divider kind="secondary" direction="horizontal" />
          {!showAgentView && <Heading variant="heading6">
            <Group direction="horizontal" horizontalAlign="space-between">
              <div>Total</div>
              <FormatCurrency value={totalPrice} />
            </Group>
          </Heading>}
          {showAgentView && <VerticalSpacer gap={4}>
            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">Total commission ({commissionAmount}%)</BodyText>
              <BodyText variant="medium" weight="bold" colour="highlight-secondary">
                -<FormatCurrency value={commissionTotal} />
              </BodyText>
            </Group>
            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">Gross total</BodyText>
              <BodyText variant="medium" weight="bold">
                <FormatCurrency value={totalPrice} />
              </BodyText>
            </Group>
            <Group direction="horizontal" horizontalAlign="space-between">
              <BodyText variant="medium" weight="bold">Net total</BodyText>
              <BodyText variant="medium" weight="bold">
                <FormatCurrency value={totalPrice - commissionTotal} />
              </BodyText>
            </Group>
          </VerticalSpacer>}
        </BreakdownList>
      </DropdownList>
    </>
  )
}

export default PricingBreakdownDropdown
